import { createRouter, createWebHashHistory } from "vue-router";
import Login from "../views/Login.vue";
import MapPage from "../views/Map.vue";
import ReportsPage from "../views/Reports.vue";
import SiteConfig from "../views/SiteConfig.vue";
import MyAccount from "../views/MyAccount.vue";
import ClientConfigOptions from "../views/ClientConfigOptions.vue";
import ClientOnBoarder from "../views/ClientManager.vue";
import GeometryImporter from "../views/GeometryImporter.vue";
import DataManager from "../views/DataManager.vue";
import UserManager from "../views/UserManager.vue";
import UserModuleGroupManager from "../views/UserModuleGroupManager.vue";
import ThemesManager from "@/views/ThemesManager.vue";
import ReportManager from "../views/ReportManager.vue";
import ReportThemes from "../views/ReportThemes.vue";
import CustomAreaManager from "../views/CustomAreaManager.vue";
import DashboardPage from "../views/Dashboard.vue";
import UserAdmin from "../views/UserAdmin.vue";
import ServicesManager from "../views/ServicesManager.vue";
import ExtraPages from "../views/ExtraPages.vue";
import ViewCustomPages from "@/views/ViewCustomPages.vue";
import CustomData from "@/views/CustomData.vue";
import GroupSettings from "@/views/GroupSettings.vue";
import DiscoveryTool from "@/views/DiscoveryTool.vue";
import DiscoveryToolMapsPage from "@/views/DiscoveryToolMapsPage.vue";
import ChartSandbox from "@/views/ChartSandbox.vue";
import ReportSandbox from "@/views/ReportSandbox.vue";
import DatastorePage from "@/views/Datastore.vue";
import DevOpsPage from "@/views/DevOps.vue";
import DevopsErrorLogDetails from "@/views/DevopsErrorLogDetails.vue";
import ServicesDirectory from "@/views/ServicesDirectory.vue";
import SDManager from "@/views/SDManager.vue";
import UserCreationInvite from "@/views/UserCreationInvite.vue";
import BuyIMDReport from "@/views/BuyIMDReport.vue";
import OnDemandIncludedThemes from "@/views/OnDemandIncludedThemes.vue";
import ImdSelectArea from "@/views/ImdSelectArea.vue";
import OnDemandCheckOut from "@/views/OnDemandCheckOut.vue";
import ThanksForPurchase from "@/views/ThanksForPurchase.vue";
import OnDemandOrderDetails from "@/views/OnDemandOrderDetails.vue";

/**
 *  Routes that don't need an authentication
 */
const startUpRoutes = [
  {
    /**
     * reserved dummy route to handle home navigation before
     * client type deep linking/redirection ready in App.vue
     **/
    path: "/preflight",
    name: "Preflight",
    component: { render: () => null },
  },
  {
    path: "/",
    name: "Home",
    component: MapPage,
  },
  {
    path: "/login/:action?",
    name: "Login",
    component: Login,
  },
  {
    path: "/view-custom-pages",
    name: "View Custom Pages",
    component: ViewCustomPages,
  },
  {
    path: "/invitation",
    name: "Invitation",
    component: UserCreationInvite,
  },
  /*
    On Demand IMD report
  */
  {
    path: "/purchase/imd-report",
    name: "English Indices of Deprivation 2025 Insights",
    component: BuyIMDReport,
  },
  {
    path: "/purchase/imd-report/included-themes",
    name: "Report outline",
    component: OnDemandIncludedThemes,
  },
  {
    path: "/purchase/imd-report/included-themes/select-area",
    name: "ImdSelectArea",
    component: ImdSelectArea,
    meta: { title: "Select your area" },
  },
  {
    path: "/purchase/imd-report/included-themes/select-area/check-out",
    name: "OnDemandCheckOut",
    component: OnDemandCheckOut,
    meta: { title: "Complete order" },
  },
  {
    path: "/purchase-successful",
    name: "ThanksForPurchase",
    component: ThanksForPurchase,
  },
  {
    path: "/order/:orderId?",
    name: "OnDemandOrderDetails",
    component: OnDemandOrderDetails,
    meta: { title: "Your report is ready" }, //set page <title> independently of route.name
  },
];

/**
 *  Routes that'll be added to the user only if they have access to them under their module group
 */
const securedRoutes = [
  {
    path: "/map",
    name: "Map",
    component: MapPage,
  },
  {
    path: "/reports/:action?",
    name: "Reports",
    component: ReportsPage,
  },
  {
    path: "/site-config",
    name: "Site Config",
    component: SiteConfig,
  },
  {
    path: "/account",
    name: "My account",
    component: MyAccount,
  },
  {
    path: "/custom-client-config-options",
    name: "Custom Client Config Options",
    component: ClientConfigOptions,
  },
  {
    path: "/client-manager",
    name: "Client Manager",
    component: ClientOnBoarder,
  },
  {
    path: "/group-settings",
    name: "Group Settings",
    component: GroupSettings,
  },
  {
    path: "/geometry-importer",
    name: "Geometry Importer",
    component: GeometryImporter,
  },
  {
    path: "/data-manager",
    name: "Data Manager",
    component: DataManager,
  },
  {
    path: "/user-manager",
    name: "User Manager",
    component: UserManager,
  },
  {
    path: "/user-module-group-manager",
    name: "User Module Group Manager",
    component: UserModuleGroupManager,
  },
  {
    path: "/custom-area-manager",
    name: "Custom Area Manager",
    component: CustomAreaManager,
  },
  {
    path: "/custom-area-manager/create/standard-area",
    name: "Create Standard Area",
    component: CustomAreaManager,
    meta: { openStandardAreaDialog: true },
  },
  {
    path: "/manage-services",
    name: "Manage Services",
    component: ServicesManager,
  },
  {
    path: "/report-manager",
    name: "Report Manager",
    component: ReportManager,
  },
  {
    path: "/report-themes",
    name: "Report Themes",
    component: ReportThemes,
  },
  {
    path: "/themes/:selectedTabRoute?/:themeId?",
    name: "Themes Manager",
    component: ThemesManager,
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: DashboardPage,
  },
  {
    path: "/user-admin",
    name: "User Admin",
    component: UserAdmin,
  },
  {
    path: "/extra-pages",
    name: "Extra Pages",
    component: ExtraPages,
  },
  {
    path: "/custom-data",
    name: "Custom Data",
    component: CustomData,
  },
  {
    path: "/discovery-tool",
    name: "Discovery Tool",
    component: DiscoveryTool,
    children: [
      {
        path: "map/:parentAreaIDs",
        name: "Discovery Tool Map",
        component: DiscoveryToolMapsPage,
      },
    ],
  },
  {
    path: "/datastore/:selectedTabRoute?",
    name: "Datastore",
    component: DatastorePage,
  },
  {
    path: "/devops/:selectedTabRoute?",
    name: "Devops",
    component: DevOpsPage,
  },
  {
    path: "/devops/devops-logs/error-log-details/:id",
    name: "Error Log Details",
    component: DevopsErrorLogDetails,
  },
  {
    path: "/services",
    name: "Services Directory",
    component: ServicesDirectory,
    children: [],
  },
  {
    path: "/sd-manager/:selectedTabRoute?",
    name: "SD Manager",
    component: SDManager,
  },
  {
    path: "/chart-sandbox",
    name: "Chart Sandbox",
    component: ChartSandbox,
  },
  {
    path: "/report-sandbox",
    name: "Report Sandbox",
    component: ReportSandbox,
  },
];

/**
 *  Set up redirects
 *  @example { path: <REDIRECT_FROM>, redirect: <TARGET> }
 */
const redirects = [
  { path: "/user-editor", redirect: "/account" }, // old url of "My account", redirect to the new one
];

// add redirects
if (redirects.length) {
  redirects.forEach((r) => {
    startUpRoutes.push(r);
  });
}

const router = createRouter({
  history: createWebHashHistory(),
  routes: startUpRoutes,
});

export { router, startUpRoutes, securedRoutes };

// Minimal route guard implement preflight on '/' (Home) to prevent Map flash for standard sites
router.beforeResolve(async (to, from, next) => {
  //cookie checks
  const hasBearer = document.cookie.includes("BEARER_TOKEN=");
  const hasPublicCookie = document.cookie.includes("OI_PUBLIC_SITE=");

  //if preflight and authenticated then proceed to home to prevent loop from app.vue
  if (to.name === "Preflight" && (hasBearer || hasPublicCookie))
    return next({ name: "Home" });

  //allow all non-home routes to proceed to App.vue
  if (to.name !== "Home") return next();

  //proceed if cookies found - auithenticated user or public site user
  if (hasBearer || hasPublicCookie) return next();

  // Unauthenticated '/' route -> convert to  lightweight Preflight (App.vue decides login or map)
  return next({ name: "Preflight", query: to.query || {} });
});
